import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Search, Navigation, Calendar } from 'lucide-react';
import { Card } from '../components/ui/Card';
const competitorColors: Record<string, string> = {
  'Apollo Optik': '#005aaa',
  'Mister Spex': '#515151',
  'pro optik': '#eb5e32',
  'eyes + more': '#f28902'
};
const competitorLogos: Record<string, string> = {
  'Apollo Optik': 'https://upload.wikimedia.org/wikipedia/de/thumb/7/7b/Apollo-Optik_logo.svg/330px-Apollo-Optik_logo.svg.png',
  'Mister Spex': 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/46/MSX-Logo-ohne-Claim_black.png/330px-MSX-Logo-ohne-Claim_black.png',
  'pro optik': 'https://www.prooptik.de/public/Marketing/Website/4736/image-thumb__4736___auto_40cd750bba9870f18aada2478b24840a/logo-optik.png',
  'eyes + more': 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6e/Eyes_and_more_Logo.svg/330px-Eyes_and_more_Logo.svg.png'
};
interface TopicItem {
  name: string;
  size: number;
  sentiment: 'positive' | 'neutral' | 'negative';
}
const competitorTopics: Array<{
  name: string;
  topics: TopicItem[];
}> = [{
  name: 'Apollo Optik',
  topics: [{
    name: 'Brillenauswahl',
    size: 845,
    sentiment: 'positive'
  }, {
    name: 'Gleitsichtgläser',
    size: 620,
    sentiment: 'positive'
  }, {
    name: 'Beratung',
    size: 590,
    sentiment: 'positive'
  }, {
    name: 'Hohe Preise',
    size: 450,
    sentiment: 'negative'
  }, {
    name: 'Lange Wartezeiten',
    size: 380,
    sentiment: 'negative'
  }, {
    name: 'Sehtest',
    size: 310,
    sentiment: 'neutral'
  }, {
    name: 'Terminvergabe',
    size: 280,
    sentiment: 'neutral'
  }]
}, {
  name: 'Mister Spex',
  topics: [{
    name: 'Online-Bestellung',
    size: 920,
    sentiment: 'positive'
  }, {
    name: 'Günstige Preise',
    size: 750,
    sentiment: 'positive'
  }, {
    name: 'Lieferzeit',
    size: 610,
    sentiment: 'negative'
  }, {
    name: 'Markenbrillen',
    size: 540,
    sentiment: 'positive'
  }, {
    name: 'Rückgabe',
    size: 480,
    sentiment: 'negative'
  }, {
    name: 'Anprobe zu Hause',
    size: 420,
    sentiment: 'neutral'
  }, {
    name: 'Kundenservice',
    size: 320,
    sentiment: 'negative'
  }, {
    name: 'Partneroptiker',
    size: 310,
    sentiment: 'neutral'
  }]
}, {
  name: 'pro optik',
  topics: [{
    name: 'Preis-Leistung',
    size: 880,
    sentiment: 'positive'
  }, {
    name: 'Fachkompetenz',
    size: 710,
    sentiment: 'positive'
  }, {
    name: 'Nulltarif-Brillen',
    size: 650,
    sentiment: 'positive'
  }, {
    name: 'Wartezeiten',
    size: 520,
    sentiment: 'negative'
  }, {
    name: 'Filialgestaltung',
    size: 410,
    sentiment: 'negative'
  }, {
    name: 'Kontaktlinsen',
    size: 390,
    sentiment: 'neutral'
  }, {
    name: 'Reparaturen',
    size: 250,
    sentiment: 'neutral'
  }]
}, {
  name: 'eyes + more',
  topics: [{
    name: 'Trendige Brillen',
    size: 890,
    sentiment: 'positive'
  }, {
    name: 'Festpreise',
    size: 760,
    sentiment: 'positive'
  }, {
    name: 'Schneller Service',
    size: 620,
    sentiment: 'positive'
  }, {
    name: 'Qualität',
    size: 580,
    sentiment: 'negative'
  }, {
    name: 'Beratung',
    size: 490,
    sentiment: 'negative'
  }, {
    name: 'Gleitsicht',
    size: 450,
    sentiment: 'neutral'
  }, {
    name: 'Auswahl',
    size: 350,
    sentiment: 'negative'
  }, {
    name: 'Aktionen',
    size: 320,
    sentiment: 'neutral'
  }]
}];
const radiusOptions = ['1 km', '2 km', '5 km', '10 km'];
const radiusTopicData: Record<string, Array<{
  name: string;
  topics: TopicItem[];
}>> = {
  'Hamburg, Jungfernstieg': [{
    name: 'Apollo Optik',
    topics: [{
      name: 'Preise',
      size: 58,
      sentiment: 'negative'
    }, {
      name: 'City-Lage',
      size: 45,
      sentiment: 'positive'
    }, {
      name: 'Wartezeit',
      size: 41,
      sentiment: 'negative'
    }, {
      name: 'Auswahl',
      size: 32,
      sentiment: 'positive'
    }]
  }, {
    name: 'Mister Spex',
    topics: [{
      name: 'Beratung',
      size: 44,
      sentiment: 'negative'
    }, {
      name: 'Store-Design',
      size: 38,
      sentiment: 'positive'
    }, {
      name: 'Termine',
      size: 35,
      sentiment: 'negative'
    }, {
      name: 'Marken',
      size: 29,
      sentiment: 'positive'
    }]
  }, {
    name: 'pro optik',
    topics: [{
      name: 'Auswahl',
      size: 61,
      sentiment: 'negative'
    }, {
      name: 'Günstig',
      size: 52,
      sentiment: 'positive'
    }, {
      name: 'Personal',
      size: 28,
      sentiment: 'negative'
    }]
  }],
  'Berlin, Alexanderplatz': [{
    name: 'Apollo Optik',
    topics: [{
      name: 'Überfüllt',
      size: 92,
      sentiment: 'negative'
    }, {
      name: 'Öffnungszeiten',
      size: 88,
      sentiment: 'positive'
    }, {
      name: 'Preise',
      size: 65,
      sentiment: 'negative'
    }, {
      name: 'Sehtest',
      size: 45,
      sentiment: 'positive'
    }]
  }, {
    name: 'eyes + more',
    topics: [{
      name: 'Qualität',
      size: 81,
      sentiment: 'negative'
    }, {
      name: 'Preise',
      size: 75,
      sentiment: 'positive'
    }, {
      name: 'Wartezeit',
      size: 66,
      sentiment: 'negative'
    }, {
      name: 'Aktionen',
      size: 54,
      sentiment: 'positive'
    }]
  }, {
    name: 'Mister Spex',
    topics: [{
      name: 'Beratung',
      size: 77,
      sentiment: 'negative'
    }, {
      name: 'Wartezeit',
      size: 59,
      sentiment: 'negative'
    }, {
      name: 'Zentral',
      size: 42,
      sentiment: 'positive'
    }]
  }],
  'München, Marienplatz': [{
    name: 'Apollo Optik',
    topics: [{
      name: 'Teuer',
      size: 88,
      sentiment: 'negative'
    }, {
      name: 'Auswahl',
      size: 65,
      sentiment: 'positive'
    }, {
      name: 'Qualität',
      size: 52,
      sentiment: 'positive'
    }, {
      name: 'Wartezeit',
      size: 45,
      sentiment: 'negative'
    }]
  }, {
    name: 'Mister Spex',
    topics: [{
      name: 'Atmosphäre',
      size: 55,
      sentiment: 'negative'
    }, {
      name: 'Marken',
      size: 48,
      sentiment: 'positive'
    }, {
      name: 'Preise',
      size: 41,
      sentiment: 'positive'
    }, {
      name: 'Personal',
      size: 32,
      sentiment: 'negative'
    }]
  }, {
    name: 'pro optik',
    topics: [{
      name: 'Parken',
      size: 71,
      sentiment: 'negative'
    }, {
      name: 'Beratung',
      size: 62,
      sentiment: 'positive'
    }, {
      name: 'Gleitsicht',
      size: 55,
      sentiment: 'positive'
    }, {
      name: 'Auswahl',
      size: 38,
      sentiment: 'negative'
    }]
  }]
};
const timePeriods = [{
  id: '30',
  label: '30 Tage'
}, {
  id: '60',
  label: '60 Tage'
}, {
  id: '120',
  label: '120 Tage'
}, {
  id: '365',
  label: '365 Tage'
}, {
  id: 'gesamt',
  label: 'Gesamt'
}];
function applyTimeFactor(value: number, period: string): number {
  if (period === 'gesamt') return value;
  const factors: Record<string, number> = {
    '30': 0.08,
    '60': 0.17,
    '120': 0.33,
    '365': 1.0
  };
  return Math.max(1, Math.round(value * (factors[period] || 1)));
}
function SimpleTreemap({
  data
}: {
  data: TopicItem[];
}) {
  const total = data.reduce((sum, item) => sum + item.size, 0);
  const sortedData = [...data].sort((a, b) => b.size - a.size);
  const getColors = (sentiment: string) => {
    switch (sentiment) {
      case 'positive':
        return {
          bg: '#d4edda',
          border: '#639754',
          text: '#1a3d1a'
        };
      case 'negative':
        return {
          bg: '#f8d7da',
          border: '#E03C32',
          text: '#3d0a0a'
        };
      default:
        return {
          bg: '#f9f5eb',
          border: '#c5a55a',
          text: '#3d3200'
        };
    }
  };
  return <div className="grid grid-cols-4 gap-1.5 h-full" data-id="element-924">
      {sortedData.map((item, index) => {
      const colors = getColors(item.sentiment);
      let colSpan = 1,
        rowSpan = 1;
      if (index === 0) {
        colSpan = 2;
        rowSpan = 2;
      } else if (index === 1 || index === 2) {
        colSpan = 2;
        rowSpan = 1;
      }
      return <div key={item.name} className="rounded-lg p-2 flex flex-col justify-center items-center text-center" style={{
        backgroundColor: colors.bg,
        border: `2px solid ${colors.border}`,
        gridColumn: `span ${colSpan}`,
        gridRow: `span ${rowSpan}`
      }} data-id="element-925">
            <span className="font-semibold text-xs leading-tight" style={{
          color: colors.text
        }} data-id="element-926">
              {item.name}
            </span>
            <span className="text-[10px] mt-0.5 opacity-70" style={{
          color: colors.text
        }} data-id="element-927">
              {item.size.toLocaleString('de-DE')}
            </span>
          </div>;
    })}
    </div>;
}
export function ThemenvergleichPage() {
  const [addressInput, setAddressInput] = useState('');
  const [selectedRadius, setSelectedRadius] = useState('5 km');
  const [selectedPeriod, setSelectedPeriod] = useState('gesamt');
  const [addressSearched, setAddressSearched] = useState('Hamburg, Jungfernstieg');
  const [hasSearched, setHasSearched] = useState(true);
  const handleSearch = () => {
    const key = Object.keys(radiusTopicData).find(k => k.toLowerCase().includes(addressInput.toLowerCase().split(',')[0].toLowerCase())) || 'Hamburg, Jungfernstieg';
    setAddressSearched(key);
    setHasSearched(true);
  };
  const rawLocalTopics = radiusTopicData[addressSearched] || radiusTopicData['Hamburg, Jungfernstieg'];
  const currentCompetitorTopics = competitorTopics.map(comp => ({
    ...comp,
    topics: comp.topics.map(t => ({
      ...t,
      size: applyTimeFactor(t.size, selectedPeriod)
    }))
  }));
  const localTopics = rawLocalTopics.map(comp => ({
    ...comp,
    topics: comp.topics.map(t => ({
      ...t,
      size: applyTimeFactor(t.size, selectedPeriod)
    }))
  }));
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-928">
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6" data-id="element-929">
        <div data-id="element-930">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-931">Themenvergleich</h1>
          <p className="text-gray-500 mt-1" data-id="element-932">
            Stärken und Schwächen der Mitbewerber basierend auf Kundenfeedback
          </p>
        </div>

        <div className="flex items-center gap-3 bg-white p-2 rounded-xl shadow-sm border border-gray-100" data-id="element-933">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-600 pl-2" data-id="element-934">
            <Calendar size={16} data-id="element-935" />
            <span data-id="element-936">Zeitraum:</span>
          </div>
          <div className="flex gap-1" data-id="element-937">
            {timePeriods.map(period => <button key={period.id} onClick={() => setSelectedPeriod(period.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${selectedPeriod === period.id ? 'bg-edeka-blue text-white shadow-sm' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`} data-id="element-938">
                {period.label}
              </button>)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-939">
        {currentCompetitorTopics.map((comp, index) => <motion.div key={comp.name} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: index * 0.1
      }} data-id="element-940">
            <Card className="h-full" data-id="element-941">
              <div className="p-6" data-id="element-942">
                <div className="flex items-center gap-3 mb-4" data-id="element-943">
                  <img src={competitorLogos[comp.name]} alt={comp.name} className="w-12 object-contain shrink-0 bg-white rounded" data-id="element-944" />
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-945">
                    {comp.name}
                  </h3>
                </div>
                <p className="text-sm text-gray-500 mb-4" data-id="element-946">
                  Häufig erwähnte Themen in Bewertungen
                </p>

                <div className="h-56" data-id="element-947">
                  <SimpleTreemap data={comp.topics} data-id="element-948" />
                </div>

                <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-gray-100" data-id="element-949">
                  <div className="flex items-center gap-2" data-id="element-950">
                    <div className="w-3 h-3 rounded" style={{
                  backgroundColor: '#639754'
                }} data-id="element-951" />
                    <span className="text-xs text-gray-600" data-id="element-952">Positiv</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-953">
                    <div className="w-3 h-3 rounded" style={{
                  backgroundColor: '#c5a55a'
                }} data-id="element-954" />
                    <span className="text-xs text-gray-600" data-id="element-955">Neutral</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-956">
                    <div className="w-3 h-3 rounded" style={{
                  backgroundColor: '#E03C32'
                }} data-id="element-957" />
                    <span className="text-xs text-gray-600" data-id="element-958">Negativ</span>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>)}
      </div>

      <Card data-id="element-959">
        <div className="p-6" data-id="element-960">
          <div className="mb-5" data-id="element-961">
            <h3 className="text-lg font-semibold text-gray-900" data-id="element-962">
              Lokale Themenanalyse
            </h3>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-963">
              Welche Themen bewegen die Kunden im direkten Umfeld?
            </p>
          </div>

          <div className="flex gap-3 mb-6 flex-wrap" data-id="element-964">
            <div className="flex-1 min-w-48 relative" data-id="element-965">
              <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-966" />
              <input type="text" placeholder="Adresse eingeben, z.B. Hamburg, Jungfernstieg" value={addressInput} onChange={e => setAddressInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-edeka-blue/20 focus:border-edeka-blue" data-id="element-967" />
            </div>
            <div className="flex items-center gap-2" data-id="element-968">
              <span className="text-sm text-gray-500 whitespace-nowrap" data-id="element-969">
                Radius:
              </span>
              <div className="flex gap-1" data-id="element-970">
                {radiusOptions.map(r => <button key={r} onClick={() => setSelectedRadius(r)} className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${selectedRadius === r ? 'bg-edeka-blue text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-971">
                    {r}
                  </button>)}
              </div>
            </div>
            <button onClick={handleSearch} className="flex items-center gap-2 px-4 py-2.5 bg-edeka-blue text-white rounded-lg text-sm font-medium hover:bg-edeka-blue/90 transition-colors" data-id="element-972">
              <Search size={14} data-id="element-973" />
              Suchen
            </button>
          </div>

          {hasSearched && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.2
        }} data-id="element-974">
              <div className="text-xs text-gray-500 mb-6 flex items-center gap-1.5" data-id="element-975">
                <Navigation size={12} data-id="element-976" />
                <span data-id="element-977">
                  Ergebnisse für{' '}
                  <strong className="text-gray-700" data-id="element-978">{addressSearched}</strong>{' '}
                  im Umkreis von{' '}
                  <strong className="text-gray-700" data-id="element-979">{selectedRadius}</strong>
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-980">
                {localTopics.map((comp, i) => <motion.div key={comp.name} initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.08
            }} className="rounded-xl border border-gray-100 bg-gray-50/50 p-4" data-id="element-981">
                    <div className="flex items-center gap-2 mb-4" data-id="element-982">
                      <img src={competitorLogos[comp.name]} alt={comp.name} className="h-5 w-5 object-contain shrink-0 bg-white rounded" data-id="element-983" />
                      <span className="font-semibold text-gray-900" data-id="element-984">
                        {comp.name}
                      </span>
                    </div>
                    <div className="h-40" data-id="element-985">
                      <SimpleTreemap data={comp.topics} data-id="element-986" />
                    </div>
                  </motion.div>)}
              </div>
            </motion.div>}
        </div>
      </Card>
    </div>;
}